import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  ArrowBack,
  Info,
} from '@mui/icons-material';
import { authService } from '@services/authService';

const steps = ['Verificare CUI', 'Informatii personale', 'Confirmare'];

const RegisterVeterinarian: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    cui: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialization: '',
    licenseNumber: '',
    experience: 0,
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCuiVerification = async () => {
    if (!formData.cui || formData.cui.length < 6) {
      setError('CUI-ul trebuie sa aiba cel putin 6 caractere');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Verifica CUI-ul in baza de date
      const response = await authService.verifyVeterinarianCui(formData.cui);
      
      if (response.success) {
        // CUI nu gasit - trece direct la urmatoarea pagina
        setActiveStep(1);
      } else {
        // CUI gasit sau alta eroare
        if (response.error && response.error.includes('deja inregistrat')) {
          setError('CUI-ul este deja inregistrat in sistem. Un reprezentant te va contacta in curand.');
        } else {
          setError(response.error || 'Eroare la verificarea CUI-ului');
        }
      }
    } catch (err: any) {
      // Verifica daca este eroare 409 (CUI exista)
      if (err.response && err.response.status === 409) {
        setError('CUI-ul este deja inregistrat in sistem. Un reprezentant te va contacta in curand.');
      } else {
        // Eroare de retea - trece direct la urmatoarea pagina
        setActiveStep(1);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      handleCuiVerification();
    } else if (activeStep === 1) {
      // Validare informatii personale
      if (!formData.firstName || !formData.lastName || !formData.email) {
        setError('Toate campurile obligatorii trebuie completate');
        return;
      }
      setActiveStep(2);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async () => {
    if (formData.password !== formData.confirmPassword) {
      setError('Parolele nu se potrivesc');
      return;
    }

    if (formData.password.length < 6) {
      setError('Parola trebuie sa aiba cel putin 6 caractere');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await authService.registerVeterinarian({
        ...formData,
        role: 'veterinarian',
        status: 'pending' // Veterinarii incep cu status pending
      });

      if (response.success) {
        setSuccess('Cererea ta a fost trimisa cu succes! Vei primi un email cand contul va fi aprobat de administrator.');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(response.error || 'Eroare la trimiterea cererii');
      }
    } catch (err) {
      setError('Eroare la trimiterea cererii. Incearca din nou.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Verificare CUI
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Introdu CUI-ul tau pentru a verifica daca esti inregistrat in baza de date a medicilor veterinari.
            </Typography>
            
            <TextField
              name="cui"
              label="CUI (Cod Unic de Identificare)"
              value={formData.cui}
              onChange={handleInputChange}
              required
              fullWidth
              disabled={loading}
              sx={{ mb: 2 }}
              placeholder="Ex: 12345678"
            />
            
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={loading || !formData.cui}
              fullWidth
            >
              {loading ? <CircularProgress size={24} /> : 'Verifica CUI'}
            </Button>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Informatii personale
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Completeaza informatiile tale personale si de contact.
            </Typography>

            <Box display="flex" gap={2} mb={2}>
              <TextField
                name="firstName"
                label="Prenume"
                value={formData.firstName}
                onChange={handleInputChange}
                required
                fullWidth
                disabled={loading}
              />
              <TextField
                name="lastName"
                label="Nume"
                value={formData.lastName}
                onChange={handleInputChange}
                required
                fullWidth
                disabled={loading}
              />
            </Box>

            <TextField
              name="email"
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              fullWidth
              disabled={loading}
              sx={{ mb: 2 }}
            />

            <TextField
              name="phone"
              label="Telefon"
              value={formData.phone}
              onChange={handleInputChange}
              fullWidth
              disabled={loading}
              sx={{ mb: 2 }}
            />

            <TextField
              name="specialization"
              label="Specializare"
              value={formData.specialization}
              onChange={handleInputChange}
              fullWidth
              disabled={loading}
              sx={{ mb: 2 }}
            />

            <TextField
              name="licenseNumber"
              label="Numarul licentei"
              value={formData.licenseNumber}
              onChange={handleInputChange}
              fullWidth
              disabled={loading}
              sx={{ mb: 3 }}
            />

            <Box display="flex" gap={2}>
              <Button onClick={handleBack} variant="outlined" fullWidth>
                Inapoi
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={loading}
                fullWidth
              >
                Continua
              </Button>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Setare parola si confirmare
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Seteaza o parola pentru contul tau si confirma informatiile.
            </Typography>

            <TextField
              name="password"
              label="Parola"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleInputChange}
              required
              fullWidth
              disabled={loading}
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              name="confirmPassword"
              label="Confirma parola"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
              fullWidth
              disabled={loading}
              sx={{ mb: 3 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  <Info sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Informatii importante
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Dupa trimiterea cererii, administratorul va primi o notificare si va aproba contul tau. 
                  Vei primi un email cand contul va fi activat.
                </Typography>
              </CardContent>
            </Card>

            <Box display="flex" gap={2}>
              <Button onClick={handleBack} variant="outlined" fullWidth>
                Inapoi
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                fullWidth
              >
                {loading ? <CircularProgress size={24} /> : 'Trimite cererea'}
              </Button>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Header */}
        <Box display="flex" alignItems="center" mb={3}>
          <IconButton onClick={() => navigate('/account-type')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" component="h1" color="secondary">
            Creeaza cont Veterinar
          </Typography>
        </Box>

        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Step Content */}
        {renderStepContent(activeStep)}

        <Box textAlign="center" mt={4}>
          <Typography variant="body2" color="text.secondary">
            Ai deja un cont?{' '}
            <Button
              variant="text"
              color="primary"
              onClick={() => navigate('/login')}
              sx={{ textTransform: 'none' }}
            >
              Conecteaza-te aici
            </Button>
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default RegisterVeterinarian; 
